import { useEffect, useState } from "react"
import Link from "next/link"
import classNames from "classnames"
import { useMutation } from "react-apollo"

import { START_PREMIUM_CHECKOUT } from "../onboarding/queries"
import {
  BILLING_KEYS,
  DEFAULT_BILLING,
  PREMIUM_FEATURES,
  PREMIUM_PLANNED,
  PREMIUM_PRICES,
  TRIAL_DAYS,
  billedText,
  savingFor,
} from "../../lib/premium-plans"

import "./upgrade-dialog.scss"

const errorText = (e, fallback) =>
  (e && e.graphQLErrors && e.graphQLErrors[0] && e.graphQLErrors[0].message) || fallback

// Back to this page after Checkout, without the params a previous return left
const herePath = () => {
  const url = new URL(window.location.href)
  url.searchParams.delete("checkout")
  url.searchParams.delete("session_id")
  return url.pathname + url.search
}

const IconCheck = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="m5 12 5 5L20 7" />
  </svg>
)

// The in-app upgrade dialog, opened by togglePremiumCofounder from anywhere in
// the app. `message` says why it opened (e.g. the daily message limit).
// Payment is Stripe Checkout, the same as /pricing and /welcome; CheckoutReturn
// in the page shell applies it when the browser comes back, to `returnPath`
// or else this page. `inline` is the old /sign-up page's plan step.
const UpgradeDialog = ({ me, message, togglePopup, returnPath, inline }) => {
  const [billing, setBilling] = useState(DEFAULT_BILLING)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [startCheckout] = useMutation(START_PREMIUM_CHECKOUT)
  const price = PREMIUM_PRICES[billing]

  // the Back button from Stripe can restore this page still busy
  useEffect(() => {
    const reset = (e) => {
      if (e.persisted) setBusy(false)
    }
    window.addEventListener("pageshow", reset)
    return () => window.removeEventListener("pageshow", reset)
  }, [])

  const go = () => {
    setBusy(true)
    setError(null)
    const path = herePath()
    startCheckout({ variables: { interval: billing, returnPath: returnPath || path, cancelPath: path } })
      .then(({ data }) => {
        window.location.assign(data.startPremiumCheckout.url)
      })
      .catch((e) => {
        setBusy(false)
        setError(errorText(e, "We couldn't open checkout. Please try again."))
      })
  }

  if (me && me.hasValidCoFounderSubscription) {
    return (
      <div className="upgrade">
        <h2 className="upgrade__title">You&rsquo;re on Premium</h2>
        <p className="upgrade__lede">Everything below is already yours. Manage billing in Settings.</p>
        <button type="button" className="upgrade__cta" onClick={togglePopup}>
          Close
        </button>
      </div>
    )
  }

  return (
    <div className={classNames("upgrade", { "upgrade--inline": inline })}>
      <h2 className="upgrade__title" id="upgrade-title">
        Premium
      </h2>
      {message ? (
        <p className="upgrade__reason">{message}</p>
      ) : (
        <p className="upgrade__lede">
          Message every founder you want to, get listed in the founder grid, and keep a FounderAssist plan that
          moves with you.
        </p>
      )}

      <div className="upgrade__periods" role="radiogroup" aria-label="How often you're billed">
        {BILLING_KEYS.map((key) => (
          <button
            key={key}
            type="button"
            role="radio"
            aria-checked={billing === key}
            className={classNames("upgrade__period", { "upgrade__period--on": billing === key })}
            onClick={() => setBilling(key)}
          >
            <span className="upgrade__period-name">{PREMIUM_PRICES[key].label}</span>
            <span className="upgrade__period-price">${PREMIUM_PRICES[key].perMonth}/mo</span>
            {savingFor(key) > 0 && <span className="upgrade__saving">Save {savingFor(key)}%</span>}
          </button>
        ))}
      </div>

      <div className="upgrade__buy">
        <p className="upgrade__price">
          <strong>${price.perMonth}</strong>
          <span>/month</span>
          {billing !== "monthly" && <span className="upgrade__billed">{billedText(billing)}</span>}
        </p>
        <button type="button" className="upgrade__cta" onClick={go} disabled={busy}>
          {busy ? "Opening checkout…" : `Start ${TRIAL_DAYS} days free`}
        </button>
        <p className="upgrade__fine">
          {TRIAL_DAYS} days free for new subscribers, then {billedText(billing)} unless you cancel. Card required.
          Secure checkout by Stripe.
        </p>
        {error && (
          <p className="upgrade__error" role="alert">
            {error}
          </p>
        )}
      </div>

      <ul className="upgrade__list">
        {PREMIUM_FEATURES.map((item) => (
          <li key={item}>
            <IconCheck />
            <span>{item}</span>
          </li>
        ))}
      </ul>
      <p className="upgrade__planned">
        <strong>Coming to Premium:</strong> {PREMIUM_PLANNED.join(", ").toLowerCase()}.
      </p>

      {!inline && window.location.pathname !== "/pricing" && (
        <Link href="/pricing">
          <a className="upgrade__compare" onClick={togglePopup}>
            Compare Free and Premium
          </a>
        </Link>
      )}
    </div>
  )
}

export default UpgradeDialog
